import { useMemo, useState } from "react";
import { createAdminHost, listAdminHosts, listAdminUsers, listSites, updateAdminHost } from "./api";
import { DateRange, SortTh } from "./ListControls";
import { nextSort, type SortState } from "./list-sort";
import { useAdminResource } from "./use-admin-resource";
import { usePaginatedResource } from "./use-paginated-resource";
import { EmptyRow } from "../dashboard/ui";
import { ImportHostsCard } from "./ImportHostsCard";
import { Pagination } from "../../components/Pagination";
import { SkeletonRows } from "../../components/Skeleton";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";
import { INVALID_PHONE_MESSAGE, isValidOptionalPhone } from "../../lib/validation";

type CreateHostForm = {
  name: string;
  email: string;
  phone: string;
  department: string;
  isActive: boolean;
  linkedUserId: string;
  siteId: string; // "" = company-wide (all sites)
};

const INITIAL_CREATE_FORM: CreateHostForm = {
  name: "",
  email: "",
  phone: "",
  department: "",
  isActive: true,
  linkedUserId: "",
  siteId: "",
};

type EditHostForm = {
  name: string;
  email: string;
  phone: string;
  department: string;
  isActive: boolean;
  linkedUserId: string;
  siteId: string; // "" = company-wide (all sites)
};

export function HostsPanel() {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [sort, setSort] = useState<SortState | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const isActive = activeFilter === "all" ? undefined : activeFilter === "active";
  const onSort = (col: string) => setSort((current) => nextSort(current, col));

  const { rows, loading, error, page, hasNext, refresh, nextPage, prevPage } = usePaginatedResource(
    (limit, offset) =>
      listAdminHosts({
        search: search.trim() || undefined,
        is_active: isActive,
        sort_by: sort?.by,
        sort_dir: sort?.dir,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit,
        offset,
      }),
    [search, activeFilter, sort, dateFrom, dateTo],
  );

  const { data: hostUsers } = useAdminResource(() => listAdminUsers({ role: "HOST" }), []);
  const { data: sites } = useAdminResource(() => listSites(), []);
  const activeSites = (sites ?? []).filter((site) => site.is_active);
  const siteName = (siteId: number | null | undefined) =>
    siteId == null ? "All sites" : sites?.find((s) => s.id === siteId)?.name ?? `Site #${siteId}`;
  // Keep a now-inactive assigned site visible in the edit dropdown -- see the
  // same helper in UsersPanel.
  const siteOptions = (selectedId: string) => {
    const opts = [...activeSites];
    if (selectedId && !opts.some((s) => String(s.id) === selectedId)) {
      const current = sites?.find((s) => String(s.id) === selectedId);
      if (current) opts.push(current);
    }
    return opts;
  };
  const linkedUserIds = useMemo(
    () => new Set(rows.map((host) => host.user_id).filter((id): id is number => id != null)),
    [rows],
  );

  function availableHostUsers(currentUserId?: number | null) {
    return (hostUsers ?? []).filter(
      (candidate) => candidate.id === currentUserId || !linkedUserIds.has(candidate.id),
    );
  }

  const [createForm, setCreateForm] = useState<CreateHostForm>(INITIAL_CREATE_FORM);
  const [createPending, setCreatePending] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingHostId, setEditingHostId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditHostForm>({
    name: "",
    email: "",
    phone: "",
    department: "",
    isActive: true,
    linkedUserId: "",
    siteId: "",
  });
  const [editPending, setEditPending] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function submitCreate() {
    if (!createForm.name.trim()) {
      setCreateError("Name is required");
      return;
    }
    if (!isValidOptionalPhone(createForm.phone)) {
      setCreateError(INVALID_PHONE_MESSAGE);
      return;
    }

    setCreatePending(true);
    setCreateError(null);
    try {
      const created = await createAdminHost({
        name: createForm.name.trim(),
        email: createForm.email.trim() || undefined,
        phone: createForm.phone.trim() || undefined,
        department: createForm.department.trim() || undefined,
        is_active: createForm.isActive,
        user_id: createForm.linkedUserId ? Number(createForm.linkedUserId) : undefined,
        site_id: createForm.siteId ? Number(createForm.siteId) : null,
      });
      showSuccess(`Created host "${created.name}"`);
      setCreateForm(INITIAL_CREATE_FORM);
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to create host";
      setCreateError(message);
      showError(message);
    } finally {
      setCreatePending(false);
    }
  }

  function startEdit(hostId: number, host: EditHostForm) {
    setEditingHostId(hostId);
    setEditForm(host);
    setEditError(null);
  }

  async function submitEdit(hostId: number, name: string, wasActive: boolean) {
    if (wasActive && !editForm.isActive) {
      const ok = await confirm({
        title: "Deactivate host?",
        message: `"${name}" will no longer be assignable to new visits.`,
        confirmLabel: "Deactivate",
        danger: true,
      });
      if (!ok) return;
    }

    if (!isValidOptionalPhone(editForm.phone)) {
      setEditError(INVALID_PHONE_MESSAGE);
      return;
    }

    setEditPending(true);
    setEditError(null);
    try {
      await updateAdminHost(hostId, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        department: editForm.department.trim(),
        is_active: editForm.isActive,
        user_id: editForm.linkedUserId ? Number(editForm.linkedUserId) : undefined,
        clear_user_id: !editForm.linkedUserId,
        ...(editForm.siteId ? { site_id: Number(editForm.siteId) } : { clear_site: true }),
      });
      showSuccess(`Updated "${name}"`);
      setEditingHostId(null);
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to update host";
      setEditError(message);
      showError(message);
    } finally {
      setEditPending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Host Management</h2>
        <span>{rows.length} rows</span>
      </header>

      <ImportHostsCard onImported={() => void refresh()} />

      <form
        className="action-form action-card"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCreate();
        }}
      >
        <label htmlFor="admin-host-name">Create Host</label>
        <div className="checkin-fields">
          <input
            id="admin-host-name"
            value={createForm.name}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Name *"
          />
          <input
            value={createForm.email}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
            placeholder="Email"
          />
          <input
            value={createForm.phone}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, phone: event.target.value }))}
            placeholder="Mobile (10 digits or +code)"
            inputMode="tel"
          />
          <input
            value={createForm.department}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, department: event.target.value }))}
            placeholder="Department"
          />
          <select
            className="search-input"
            value={createForm.linkedUserId}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, linkedUserId: event.target.value }))}
          >
            <option value="">No linked user</option>
            {availableHostUsers().map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.username}
              </option>
            ))}
          </select>
          <select
            className="search-input"
            value={createForm.siteId}
            onChange={(event) => setCreateForm((prev) => ({ ...prev, siteId: event.target.value }))}
            aria-label="Site"
          >
            <option value="">All sites (company-wide)</option>
            {activeSites.map((site) => (
              <option key={site.id} value={String(site.id)}>
                {site.name}
              </option>
            ))}
          </select>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={createForm.isActive}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, isActive: event.target.checked }))}
            />
            Active
          </label>
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={createPending}>
          {createPending ? "Creating..." : "Create Host"}
        </button>
      </form>
      {createError ? <p className="error-banner action-error">{createError}</p> : null}

      <div className="filter-row">
        <input
          className="search-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, email, phone, or department"
        />
        <select
          className="search-input"
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as "all" | "active" | "inactive")}
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} label="Added" />
      </div>

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <SortTh label="Name" col="name" sort={sort} onSort={onSort} />
              <SortTh label="Email" col="email" sort={sort} onSort={onSort} />
              <th>Phone</th>
              <SortTh label="Department" col="department" sort={sort} onSort={onSort} />
              <th>Linked User</th>
              <th>Site</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={4} colSpan={8} />
            ) : rows.length === 0 ? (
              <EmptyRow label="No hosts found" colSpan={8} />
            ) : (
              rows.map((host) =>
                editingHostId === host.id ? (
                  <tr key={`host-edit-${host.id}`}>
                    <td colSpan={8}>
                      <div className="checkin-fields">
                        <input
                          value={editForm.name}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                          placeholder="Name *"
                        />
                        <input
                          value={editForm.email}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, email: event.target.value }))}
                          placeholder="Email"
                        />
                        <input
                          value={editForm.phone}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, phone: event.target.value }))}
                          placeholder="Mobile (10 digits or +code)"
                          inputMode="tel"
                        />
                        <input
                          value={editForm.department}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, department: event.target.value }))}
                          placeholder="Department"
                        />
                        <select
                          className="search-input"
                          value={editForm.linkedUserId}
                          onChange={(event) =>
                            setEditForm((prev) => ({ ...prev, linkedUserId: event.target.value }))
                          }
                        >
                          <option value="">No linked user</option>
                          {availableHostUsers(host.user_id).map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.username}
                            </option>
                          ))}
                        </select>
                        <select
                          className="search-input"
                          value={editForm.siteId}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, siteId: event.target.value }))}
                          aria-label="Edit site"
                        >
                          <option value="">All sites (company-wide)</option>
                          {siteOptions(editForm.siteId).map((site) => (
                            <option key={site.id} value={String(site.id)}>
                              {site.name}
                              {site.is_active ? "" : " (inactive)"}
                            </option>
                          ))}
                        </select>
                        <label className="checkbox-field">
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                          />
                          Active
                        </label>
                        <div className="action-cell">
                          <button
                            className="refresh-btn"
                            type="button"
                            disabled={editPending}
                            onClick={() => void submitEdit(host.id, host.name, host.is_active)}
                          >
                            {editPending ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="refresh-btn danger-action-btn"
                            type="button"
                            onClick={() => setEditingHostId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      {editError ? <p className="error-banner action-error">{editError}</p> : null}
                    </td>
                  </tr>
                ) : (
                  <tr key={`host-${host.id}`}>
                    <td>{host.name}</td>
                    <td>{host.email || "-"}</td>
                    <td>{host.phone || "-"}</td>
                    <td>{host.department || "-"}</td>
                    <td>{host.username || "-"}</td>
                    <td>{siteName(host.site_id)}</td>
                    <td>
                      <span className={host.is_active ? "status-pill status-approved" : "status-pill status-rejected"}>
                        {host.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="refresh-btn"
                        type="button"
                        onClick={() =>
                          startEdit(host.id, {
                            name: host.name,
                            email: host.email || "",
                            phone: host.phone || "",
                            department: host.department || "",
                            isActive: host.is_active,
                            linkedUserId: host.user_id != null ? String(host.user_id) : "",
                            siteId: host.site_id != null ? String(host.site_id) : "",
                          })
                        }
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} hasNext={hasNext} onPrev={prevPage} onNext={nextPage} disabled={loading} />
    </article>
  );
}
